import React, { forwardRef, useState, useRef, useImperativeHandle, useMemo } from "react";
import { FORM_ACTIONS } from "../FormActions";
import styles from "./KpiForm.module.sass";
import HeadersTab from "./Tabs/HeadersTab";
import { TabSelector } from "../../../components/index";
import DescriptionsTab from "./Tabs/DescriptionsTab";
import CommandsTab from "./Tabs/CommandsTab";
import ConfigsTab from "./Tabs/ConfigsTab";
import PackageDropdownMultiSelector from "../Packages/PackageDropdownMultiSelector/PackageDropdownMultiSelector";

const KpiForm = (props, ref) => {
    const { data, status, onChange, onSave, classifiers, querySelectors, operationType, packages, kpisList } = props;
    const {
        id,
        KpiID,
        KpiLabel,
        KpiType,
        Description,
        CreatedAt,
        UpdatedAt,
        Content,
        Metadata,
        Config,
        Packages: selectedPackages,
    } = data || {};

    const kpis = kpisList?.items;
    const [contentFormatted, setContentFormatted] = useState("{}");
    const [selected, setSelected] = useState(0);
    const formRef = useRef();
    const formTabs = ["Headers", "Descriptions", "Commands", "Configurations", "Packages"];

    useImperativeHandle(ref, () => ({
        submit: () => {
            formRef.current.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
        },
    }));

    useMemo(() => {
        try {
            if (Content) {
                setContentFormatted(JSON.stringify(JSON.parse(Content)));
            }
        } catch (e) {
            console.log(e);
        }
    }, [Content]);

    function statusCheck(modalStatus) {
        return modalStatus !== "loading";
    }

    const handleChange = (e, prop, value) => {
        onChange &&
            onChange({
                originalEvent: e,
                target: {
                    value: {
                        data: {
                            ...data,
                            [prop]: value,
                        },
                    },
                },
            });
    };

    const handleConfigsChange = (e, key, value, index, array) => {
        array[index][`${key}`] = value;
        onChange &&
            onChange({
                originalEvent: e,
                target: {
                    value: {
                        data: {
                            ...data,
                            Config: JSON.stringify([...(array || [])]),
                        },
                    },
                },
            });
    };

    return statusCheck(status) ? (
        <form
            ref={formRef}
            onSubmit={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (formRef.current.checkValidity()) {
                    data["Content"] = contentFormatted;
                    onSave && onSave();
                } else {
                    formRef.current.reportValidity();
                }
            }}
        >
            <TabSelector tabs={formTabs} selected={selected} setSelected={setSelected} />
            <div className={`${styles["inputs"]} ${styles[`visible-${selected}`]}`}>
                <HeadersTab
                    KpiID={KpiID}
                    KpiType={KpiType}
                    CreatedAt={CreatedAt}
                    UpdatedAt={UpdatedAt}
                    handleChange={handleChange}
                />
                <DescriptionsTab
                    KpiLabel={KpiLabel}
                    Description={Description}
                    handleChange={handleChange}
                />
                <CommandsTab
                    Content={contentFormatted}
                    Metadata={Metadata}
                    querySelectors={querySelectors}
                    handleChange={handleChange}
                    operationTypes={operationType}
                />
                <ConfigsTab
                    Config={Config}
                    Classifiers={classifiers}
                    handleConfigsChange={handleConfigsChange}
                    handleChange={handleChange}
                    kpisList={kpis}
                />
                <PackageDropdownMultiSelector
                    packages={packages}
                    selectedPackages={selectedPackages}
                    handleChange={handleChange}
                    entitieID={id}
                    containerTitle={"Packages"}
                    entitieKey={"workerTypeId"}
                />
            </div>
        </form>
    ) : null;
};

const KpiFormRef = forwardRef(KpiForm);

KpiFormRef.queries = (args) => {
    return [
        {
            resource: "classifiers",
            args: {
                locale: args.locale,
            },
            body: `
            level{id, name},
            appliesTo{id, name, config},
            appliesToVersion{id, name}
			      `,
        },
        {
            resource: "query_selectors",
            body: 'items{id, name}',
            args: {
                type: "kpis"
            }
        },
        {
            resource: "operation_type_kpi",
            body: 'id, name',
        },
        {
            resource: "getPackages",
            body: "id,name",
        },
        {
            resource: "kpis",
            args: {
                locale: args.locale,
            },
            body: "items{id,KpiID}",
        },
        args && args.id
            ? {
                resource: "kpi",
                args: {
                    kpiId: args.id,
                },
                body: "__typename,id,KpiID,KpiLabel,KpiType,Content,Description,AppliesTo,CreatedAt,UpdatedAt,Metadata,Config,Packages{id, packageId, workerTypeId}",
            }
            : undefined,
    ].filter((q) => q);
};

KpiFormRef.dataMapper = ["classifiers", "querySelectors", "operationType", "packages", "kpisList", "data"];

KpiFormRef.actions = [FORM_ACTIONS.SAVE];

export default KpiFormRef;
